/**
 * Demand-adjustment panel — the audit trail for quantity the scheduler
 * DELETED from the plan.
 *
 * Why this exists: the decoder's small-remainder rule
 * (`RunConfig::small_batch_drop_hours`, production default 2h, plus the 10%
 * `small_batch_drop_max_fraction` gate) drops a max-run split remainder
 * rather than opening a fresh kitchen batch and paying its CIP clean storm.
 * The dropped units are *credited against demand*, so the engine reports
 * `unmet_demand: 0` for them — but they are genuinely not being made.
 *
 * The demand-fulfilment views already show the gap, because they derive
 * `demand - produced` from the schedule rows. What they cannot show is
 * WHY: a deliberate policy drop and a genuine capacity failure look
 * identical there, and they call for completely different responses. This
 * panel is the difference.
 *
 * Run-time only. `demand_adjustments` arrives on the Rust API's job result
 * and is not persisted to the schedule record, so it is visible for the run
 * that produced it and cannot be recovered afterwards.
 */
import { memo, useMemo, useState } from 'react'
import {
  Box,
  Paper,
  Typography,
  Chip,
  Collapse,
  IconButton,
  Tooltip,
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import ContentCutIcon from '@mui/icons-material/ContentCut'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { DemandAdjustment } from './Scheduler.data'

const fmtUnits = (n: number) =>
  n >= 1000 ? `${(n / 1000).toFixed(1)}k` : n.toFixed(0)

type Props = {
  adjustments: DemandAdjustment[] | undefined
  /**
   * The engine's reported unmet demand for the same run. Shown next to the
   * dropped total so the two numbers stop contradicting each other — the
   * engine legitimately reports 0 unmet while having deleted quantity.
   */
  unmetDemand?: number
}

export const ScheduleDemandAdjustments = memo(
  ({ adjustments, unmetDemand }: Props) => {
    const [expanded, setExpanded] = useState(true)

    const totals = useMemo(() => {
      if (!adjustments?.length) return null
      return {
        count: adjustments.length,
        units: adjustments.reduce((s, a) => s + (a.quantity ?? 0), 0),
        hours: adjustments.reduce((s, a) => s + (a.hours_equivalent ?? 0), 0),
        products: new Set(adjustments.map((a) => a.product_id)).size,
      }
    }, [adjustments])

    // Nothing dropped is the normal, healthy case — render nothing at all
    // rather than an empty "0 adjustments" box that trains people to ignore
    // this area.
    if (!totals) return null

    return (
      <Paper
        variant="outlined"
        sx={{
          mt: 1.5,
          p: 1.25,
          borderRadius: 1.5,
          borderColor: 'warning.main',
          borderWidth: 2,
          bgcolor: 'rgba(255, 152, 0, 0.05)',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            cursor: 'pointer',
          }}
          onClick={() => setExpanded((v) => !v)}
        >
          <ContentCutIcon sx={{ fontSize: 18, color: 'warning.dark' }} />
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 700, color: 'warning.dark' }}
          >
            Demand dropped by the small-batch rule
          </Typography>
          <Chip
            size="small"
            label={`${fmtUnits(totals.units)} units`}
            sx={{
              height: 20,
              fontWeight: 700,
              bgcolor: 'warning.main',
              color: 'common.white',
            }}
          />
          <Chip
            size="small"
            variant="outlined"
            label={`${totals.count} adjustment${totals.count === 1 ? '' : 's'} · ${totals.products} product${totals.products === 1 ? '' : 's'} · ${totals.hours.toFixed(1)}h`}
            sx={{ height: 20 }}
          />
          <Box sx={{ flex: 1 }} />
          <IconButton size="small" aria-label="Toggle demand adjustments">
            {expanded ? (
              <ExpandLessIcon fontSize="small" />
            ) : (
              <ExpandMoreIcon fontSize="small" />
            )}
          </IconButton>
        </Box>

        <Collapse in={expanded}>
          <Typography
            variant="caption"
            sx={{ display: 'block', mt: 0.75, color: 'text.secondary' }}
          >
            These quantities are <strong>not scheduled</strong> and will not be
            produced. The engine credits them against demand, so it reports
            {typeof unmetDemand === 'number'
              ? ` unmet demand ${unmetDemand.toFixed(1)}`
              : ' zero unmet demand'}{' '}
            — the shortfall you see in Demand Fulfilment for these products is
            this rule, not a capacity failure.
          </Typography>

          <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {adjustments!.map((adj, i) => (
              <Box
                key={`${adj.product_id}-${adj.kitchen}-${i}`}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  px: 1,
                  py: 0.5,
                  borderRadius: 1,
                  bgcolor: 'common.white',
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 700, minWidth: 90, fontFamily: 'monospace' }}
                >
                  {adj.product_id}
                </Typography>
                <Chip
                  size="small"
                  variant="outlined"
                  label={adj.kitchen}
                  sx={{ height: 18, fontSize: 11 }}
                />
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 700, color: 'warning.dark', minWidth: 78 }}
                >
                  −{adj.quantity.toFixed(1)} units
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ color: 'text.secondary', minWidth: 52 }}
                >
                  {adj.hours_equivalent.toFixed(2)}h
                </Typography>
                <Box sx={{ flex: 1 }} />
                <Tooltip title={adj.reason} placement="top">
                  <InfoOutlinedIcon
                    sx={{ fontSize: 15, color: 'text.disabled' }}
                  />
                </Tooltip>
              </Box>
            ))}
          </Box>

          <Typography
            variant="caption"
            sx={{ display: 'block', mt: 0.75, color: 'text.disabled' }}
          >
            Shown for this run only — demand adjustments are not saved with the
            schedule.
          </Typography>
        </Collapse>
      </Paper>
    )
  }
)

ScheduleDemandAdjustments.displayName = 'ScheduleDemandAdjustments'
